import { Link } from 'react-router-dom';
import { useSiteConfig } from '../../contexts/SiteConfigContext';
import { cn } from '../../lib/utils';
import { appendPermanentKreedzLink, isValidFooterLink } from '../../lib/footerLinks';
import kzcompLogo from '../../kzcomp.png';

const DiscordIcon = ({ className }) => (
  <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden="true">
    <path d="M20.317 4.369A19.79 19.79 0 0 0 16.558 3.1a.074.074 0 0 0-.078.038 13.77 13.77 0 0 0-.61 1.248 18.32 18.32 0 0 0-5.487 0 12.77 12.77 0 0 0-.618-1.248.077.077 0 0 0-.079-.038A19.736 19.736 0 0 0 5.928 4.37a.07.07 0 0 0-.032.027C2.54 9.09 1.62 13.69 2.066 18.23a.083.083 0 0 0 .031.056 19.9 19.9 0 0 0 5.986 3.03.077.077 0 0 0 .084-.028c.462-.63.872-1.295 1.225-1.994a.076.076 0 0 0-.041-.105 13.1 13.1 0 0 1-1.872-.893.077.077 0 0 1-.008-.128c.126-.094.252-.192.372-.291a.074.074 0 0 1 .077-.01c3.927 1.793 8.18 1.793 12.062 0a.073.073 0 0 1 .078.009c.12.099.246.198.373.292a.077.077 0 0 1-.006.128 12.3 12.3 0 0 1-1.873.892.077.077 0 0 0-.04.106c.36.698.77 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.84 19.84 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.673-3.548-13.8a.061.061 0 0 0-.03-.029ZM8.02 15.33c-1.182 0-2.157-1.086-2.157-2.42 0-1.333.955-2.42 2.157-2.42 1.21 0 2.176 1.096 2.157 2.42 0 1.334-.956 2.42-2.157 2.42Zm7.974 0c-1.183 0-2.157-1.086-2.157-2.42 0-1.333.955-2.42 2.157-2.42 1.21 0 2.176 1.096 2.157 2.42 0 1.334-.946 2.42-2.157 2.42Z" />
  </svg>
);

const TwitchIcon = ({ className }) => (
  <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden="true">
    <path d="M2.149 0 .537 4.119v17.202h5.731V24h3.224l2.687-2.679h4.837l6.448-6.436V0H2.149Zm2.687 2.148h16.115v11.611l-3.761 3.763h-5.731l-2.687 2.679v-2.679H4.836V2.148Zm5.731 10.537h2.149V6.269h-2.149v6.416Zm5.373 0h2.149V6.269h-2.149v6.416Z" />
  </svg>
);

const YoutubeIcon = ({ className }) => (
  <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden="true">
    <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136C4.495 20.455 12 20.455 12 20.455s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814ZM9.546 15.57V8.43L15.818 12l-6.272 3.57Z" />
  </svg>
);

const socialIcons = {
  discord: DiscordIcon,
  twitch: TwitchIcon,
  youtube: YoutubeIcon,
};

const isExternal = (path) => /^(https?:|mailto:|tel:|\/\/)/i.test(path || '');
const linkClass = 'text-sm text-foreground/80 transition-colors hover:text-foreground';
const legalLinks = [
  { label: 'Legal Notice', path: '/imprint' },
  { label: 'Privacy Policy', path: '/privacy' },
  { label: 'Terms of Service', path: '/terms' },
];

const Footer = () => {
  const { config } = useSiteConfig();
  const footer = config?.footer || {};
  const year = new Date().getFullYear();

  const activeSocials = (Array.isArray(footer.socials) ? footer.socials : []).filter(
    (s) => s && typeof s.url === 'string' && s.url.trim()
  );
  const columns = (Array.isArray(footer.columns) ? footer.columns : []).filter(
    (c) => c && c.heading && Array.isArray(c.links) && c.links.some(isValidFooterLink)
  );
  const staffLink = { label: 'Staff & Contributors', path: '/staff' };
  const contactColumnIndex = columns.findIndex(
    (c) =>
      /contact/i.test(c.heading || '') || c.links.some((l) => l && /contact/i.test(l.label || ''))
  );
  const staffColumnIndex = contactColumnIndex >= 0 ? contactColumnIndex : columns.length - 1;
  const columnsWithStaffLink =
    columns.length === 0
      ? [{ heading: 'About', links: [staffLink] }]
      : columns.map((c, i) =>
          i === staffColumnIndex ? { ...c, links: [...c.links, staffLink] } : c
        );
  const columnsWithPermanentKreedzLink = appendPermanentKreedzLink(columnsWithStaffLink);

  return (
    <footer className="border-t border-border/60 bg-background">
      <div className="mx-auto max-w-screen-2xl px-4 py-12 sm:px-6">
        <div className="grid grid-cols-1 gap-10 md:grid-cols-4">
          <div className="md:col-span-1">
            <Link to="/" className="inline-flex items-center gap-2">
              <img src={kzcompLogo} alt="KZCOMP" className="h-8 w-auto" />
            </Link>
            {footer.tagline && (
              <p className="mt-4 max-w-xs text-sm text-muted-foreground">{footer.tagline}</p>
            )}

            {activeSocials.length > 0 && (
              <div className="mt-6 flex items-center gap-3">
                {activeSocials.map(({ platform, url }) => {
                  const Icon = socialIcons[platform];
                  if (!Icon) return null;
                  return (
                    <a
                      key={platform}
                      href={url}
                      target="_blank"
                      rel="noreferrer noopener"
                      className={cn(
                        'flex h-9 w-9 items-center justify-center rounded-md border border-border/60 text-muted-foreground transition-colors',
                        'hover:border-foreground/40 hover:text-foreground'
                      )}
                      aria-label={platform}
                    >
                      <Icon className="h-4 w-4" />
                    </a>
                  );
                })}
              </div>
            )}
          </div>

          {columnsWithPermanentKreedzLink.map((column) => (
            <div key={column.heading}>
              <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                {column.heading}
              </h3>
              <ul className="mt-4 space-y-2.5">
                {column.links.filter(isValidFooterLink).map((link) => (
                  <li key={`${column.heading}-${link.label}`}>
                    {isExternal(link.path) ? (
                      <a
                        href={link.path}
                        target="_blank"
                        rel="noreferrer noopener"
                        className={linkClass}
                      >
                        {link.label}
                      </a>
                    ) : (
                      <Link to={link.path} className={linkClass}>
                        {link.label}
                      </Link>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="mt-10 flex flex-col items-start justify-between gap-2 border-t border-border/60 pt-6 text-xs text-muted-foreground sm:flex-row sm:items-center">
          <p>
            © {year} {footer.copyright || 'KZcomp.com'}. All rights reserved.
          </p>
          <nav aria-label="Legal" className="flex flex-wrap items-center gap-x-4 gap-y-1">
            {legalLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className="transition-colors hover:text-foreground"
              >
                {link.label}
              </Link>
            ))}
          </nav>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
